import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  register: (data) => api.post('/auth/register', data),
  getUsers: () => api.get('/auth/users'),
  getPending: () => api.get('/auth/pending'),
  approveUser: (id) => api.post(`/auth/approve/${id}`),
  rejectUser: (id) => api.delete(`/auth/reject/${id}`),
  resetPassword: (id, password) => api.post(`/auth/reset-password/${id}`, { password }),
  changePassword: (data) => api.post('/auth/change-password', data),
};

export const firmsAPI = {
  getAll: () => api.get('/firms'),
  create: (data) => api.post('/firms', data),
  update: (id, data) => api.put(`/firms/${id}`, data),
  delete: (id) => api.delete(`/firms/${id}`),
  uploadLogo: (id, file) => {
    const fd = new FormData(); fd.append('logo', file);
    return api.post(`/firms/${id}/logo`, fd);
  },
};

export const txAPI = {
  getAll: (firmId, params) => api.get(`/firms/${firmId}/transactions`, { params }),
  getSummary: (firmId, params) => api.get(`/firms/${firmId}/transactions/summary`, { params }),
  getCategories: (firmId) => api.get(`/firms/${firmId}/transactions/categories`),
  create: (firmId, data) => {
    const fd = new FormData();
    Object.entries(data).forEach(([k,v]) => { if (v !== null && v !== undefined) fd.append(k, v); });
    return api.post(`/firms/${firmId}/transactions`, fd);
  },
  update: (firmId, id, data) => {
    const fd = new FormData();
    Object.entries(data).forEach(([k,v]) => { if (v !== null && v !== undefined) fd.append(k, v); });
    return api.put(`/firms/${firmId}/transactions/${id}`, fd);
  },
  delete: (firmId, id) => api.delete(`/firms/${firmId}/transactions/${id}`),
  receiptUrl: (firmId, id) => `/api/firms/${firmId}/transactions/${id}/receipt`,
};

export const settingsAPI = {
  getCurrencies: (firmId) => api.get(`/firms/${firmId}/currencies`),
  addCurrency: (firmId, data) => api.post(`/firms/${firmId}/currencies`, data),
  deleteCurrency: (firmId, code) => api.delete(`/firms/${firmId}/currencies/${code}`),
  getCategories: (firmId) => api.get(`/firms/${firmId}/categories`),
  addCategory: (firmId, data) => api.post(`/firms/${firmId}/categories`, data),
  deleteCategory: (firmId, id) => api.delete(`/firms/${firmId}/categories/${id}`),
  getLiveRates: () => api.get('/exchange/rates'),
  syncCurrencies: (firmId) => api.post(`/firms/${firmId}/currencies/sync`),
};

export const agencyAPI = {
  getAll: (firmId, params) => api.get(`/firms/${firmId}/agency`, { params }),
  create: (firmId, data) => api.post(`/firms/${firmId}/agency`, data),
  update: (firmId, id, data) => api.put(`/firms/${firmId}/agency/${id}`, data),
  delete: (firmId, id) => api.delete(`/firms/${firmId}/agency/${id}`),
  getSummary: (firmId, params) => api.get(`/firms/${firmId}/agency/summary`, { params }),
};

export const packageToursAPI = {
  getAll: (firmId) => api.get(`/firms/${firmId}/package-tours`),
  get: (firmId, id) => api.get(`/firms/${firmId}/package-tours/${id}`),
  create: (firmId, data) => api.post(`/firms/${firmId}/package-tours`, data),
  update: (firmId, id, data) => api.put(`/firms/${firmId}/package-tours/${id}`, data),
  delete: (firmId, id) => api.delete(`/firms/${firmId}/package-tours/${id}`),
  getDays: (firmId, id) => api.get(`/firms/${firmId}/package-tours/${id}/days`),
  addDay: (firmId, id, data) => api.post(`/firms/${firmId}/package-tours/${id}/days`, data),
  deleteDay: (firmId, id, dayId) => api.delete(`/firms/${firmId}/package-tours/${id}/days/${dayId}`),
};

export const staffAPI = {
  getAll: (firmId) => api.get(`/firms/${firmId}/staff`),
  get: (firmId, id) => api.get(`/firms/${firmId}/staff/${id}`),
  create: (firmId, data) => api.post(`/firms/${firmId}/staff`, data),
  update: (firmId, id, data) => api.put(`/firms/${firmId}/staff/${id}`, data),
  delete: (firmId, id) => api.delete(`/firms/${firmId}/staff/${id}`),
  getPayments: (firmId, id) => api.get(`/firms/${firmId}/staff/${id}/payments`),
  addPayment: (firmId, id, data) => api.post(`/firms/${firmId}/staff/${id}/pay`, data),
  deletePayment: (firmId, id, payId) => api.delete(`/firms/${firmId}/staff/${id}/payments/${payId}`),
};

export default api;
